import cn from 'classnames';
import { ChangeEvent, FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { useNavigate } from 'react-router-dom';
import packageJson from 'src/../package.json';
import { Button } from 'src/components/common/Button';
import SuccessfullyReged from 'src/components/common/SuccessfullyReged';
import { SvgIcon } from 'src/components/common/SvgIcon';
import { icons } from 'src/configs/icons';
import { makeSignInPayload, makeSignUpPayload } from 'src/helpers/transformers';
import { envStrToBoolean, getField, getFromQueryParams, getSkinId, scrollToTop } from 'src/helpers/utils';
import { payloadIsValid } from 'src/helpers/validationScheme';
import { useConnectedAction } from 'src/hooks/use-connected-action';
import { useSessionRecorder } from 'src/hooks/use-session-recorder';
import { useCurrentPlayerLimits } from 'src/hooks/useCurrentPlayerLimits';
import { useEnterSubmit } from 'src/hooks/useEnterSubmit';
import { checkAccountUniquenessRequest, confirmTermsAcceptance, signUpRequest } from 'src/services/authorizationApi';
import { setSelectedCategory } from 'src/store/app/actions';
import { openDialog } from 'src/store/dialog/actions';
import { EnumDialogsKeys } from 'src/store/dialog/types';
import { setCPF_User, setError, signIn as signInAction, toggleActivateBtn } from 'src/store/user/actions';
import { ISignUpRes } from 'src/types/res-dto/authorization';
import { RootState } from 'src/types/store-types';
import { CASINO_CATEGORIES, LEGITIMUZ_TOKEN } from 'src/utils/constants';
import SignupForm from './SignupForm';
import './styles.scss';

interface IProps extends ISignUpRes {
  setContent: (action: 'open' | 'close', content: 'login' | 'registration' | 'forgot' | null) => void;
}

// Kept outside the component: the SDK instance outlives the refs of the component that created it
let activeKycSdkInstance: any = null;
let kycSuccessHandled = false;

const SignUpContent: FC<IProps> = ({ setContent, registrationSteps, forgotLink, signIn, styles, showPreviousBtn }) => {
  const { t }: Translation = useTranslation();
  const sessionRecorder = useSessionRecorder();
  const _openDialog = useConnectedAction(openDialog);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { search } = useLocation();
  const skinId = getSkinId();
  const formRef = useRef<HTMLDivElement | null>(null);
  const registeredUsernameRef = useRef<string>('');
  const isKycEnableForReg = envStrToBoolean(process.env.REACT_APP_KYC_ENABLE);
  const isLimitsEnabled = envStrToBoolean(process.env.REACT_APP_LIMITS_ENABLE);
  const isTermsCheckEnabled = envStrToBoolean(process.env.REACT_APP_CONFIRM_TERMS_ACCEPTANCE);

  const { getCurrentPlayerLimits } = useCurrentPlayerLimits();

  const queryParams = new URLSearchParams(search);
  const urlQuery = getFromQueryParams(search);
  let affiliateId: any = '';
  if (queryParams.get('affiliateId')) {
    affiliateId = queryParams.get('affiliateId');
  } else if (sessionStorage.getItem('affiliateParams')) {
    affiliateId = JSON.parse(sessionStorage.getItem('affiliateParams') as any)?.id;
  }

  const _toggleActivateBtn = useConnectedAction(toggleActivateBtn);
  const _signInReq = useConnectedAction(signInAction.req);
  const _setCPF_User = useConnectedAction(setCPF_User);
  // const _setCPFValue = useConnectedAction(setCPFValue);

  const _setError = useConnectedAction(setError);
  const _setSelectedCategory = useConnectedAction(setSelectedCategory);
  const {
    errors: signUpErrors,
    loading,
    activateButton,
    cpfValue,
    activateNextStep,
    phoneCode,
    activateFinalNextStep,
    successfullyRegFromOtp,
  } = useSelector((state: RootState) => state.user);
  const { generalConfigs } = useSelector((state: RootState) => state.configs);

  const [activeStep, setActiveStep] = useState<number>(1);
  const [successfullyReged, setSuccessfullyReged] = useState<boolean>(false);
  const [checkEmail, setCheckEmail] = useState<boolean>(false);
  const [localErrorsState, setLocaleErrorsState] = useState<{ [key: string]: string } | null>(null);
  const [formValues, setFormValues] = useState<any>([]);
  const [requiredFields, setRequiredFields] = useState<string[]>([]);
  const [signUpRequestLoading, setSignUpRequestLoading] = useState<boolean>(false);
  const defaultCategory = process.env.REACT_APP_DEFAULT_CATEGORY;
  const isEnableOtp = envStrToBoolean(process.env.REACT_APP_VERIFY_EMAIL_BY_OTP);
  const isEnablePhoneNumberOtp = envStrToBoolean(process.env.REACT_APP_PHONE_NUMBER_VERIFICATION_FROM_REG);

  const classes = `${cn('primaryButton', 'success-filled-btn', { ['disabled']: signUpRequestLoading })}`;

  const changeContent = (content: 'forgot' | 'login') => (): void => {
    setContent('open', content);
  };

  const checkUniquenessAndOpenOtpPopUp = (email: string, cpf: string): void => {
    Promise.all([
      checkAccountUniquenessRequest({ skinId, key: 'email', value: email }),
      checkAccountUniquenessRequest({ skinId, key: 'nationalId', value: cpf }),
    ])
      .then(([emailCheckResult, nationalIdCheckResult]) => {
        if (!emailCheckResult.success || !nationalIdCheckResult?.success) {
          console.error('Failed to check account uniqueness.');
          return;
        }
        const emailExists = emailCheckResult?.result?.isExisting;
        const cpfExists = nationalIdCheckResult?.result?.isExisting;

        if (emailExists) _setError({ ...signUpErrors, email: t('emailAlreadyExists') });
        if (cpfExists) _setError({ ...signUpErrors, cpf: t('national_id_used') });
        if (emailExists || cpfExists) return;

        setActiveStep(1);
        _openDialog({
          dialogType: EnumDialogsKeys.OTP_REQUEST_POPUP,
          dialogProps: { email, cpf },
        });
      })
      .catch((error) => {
        console.error('Error checking account uniqueness:', error);
      });
  };

  const checkUniquenessOfPhoneNumber = async (phoneNumber: string, cpf: string): Promise<void> => {
    try {
      const phoneNumberCheckResult = await checkAccountUniquenessRequest({
        skinId,
        key: 'phoneNumber',
        value: phoneCode.slice(1) + phoneNumber,
      });

      if (!phoneNumberCheckResult.success) {
        console.error('Failed to check account uniqueness.');
        return;
      }

      const phoneNumberExists = phoneNumberCheckResult?.result?.isExisting;

      if (phoneNumberExists) {
        _setError({ ...signUpErrors, phoneNumber: t('phoneNumberAlreadyExists') });
        return;
      }
      setActiveStep(2);
      _openDialog({
        dialogType: EnumDialogsKeys.OTP_REQUEST_PHONE_POPUP,
        dialogProps: { phoneNumber: phoneCode.slice(1) + phoneNumber, cpf },
      });
    } catch (error) {
      console.error('Error checking account uniqueness:', error);
    }
  };

  const handleNavigateInStepper = (step: number): void => {
    scrollToTop();
    const field = getField(formValues, 'CPF');
    if (field && !Boolean(field['CPF']?.value)) {
      setLocaleErrorsState({ CPF: 'requiredField' });
    } else {
      const emailField = getField(formValues, 'email');
      const nationalIdField = getField(formValues, 'nationalId');
      const phoneNumberField = getField(formValues, 'phoneNumber');
      const nationalIdValue = nationalIdField?.nationalId?.value;
      if (isEnableOtp && step === 2) {
        const emailValue = emailField?.email?.value;
        checkUniquenessAndOpenOtpPopUp(emailValue, cpfValue || nationalIdValue);
      } else if (isEnablePhoneNumberOtp && step === 3) {
        const phoneNumberValue = phoneNumberField?.phoneNumber?.value;
        checkUniquenessOfPhoneNumber(phoneNumberValue, cpfValue || nationalIdValue).catch((e) => console.error(e));
      } else {
        setActiveStep(step);
      }
    }

    formRef?.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleUnprocessableEntityErrors = (message: any): void => {
    const params = message?.params;
    if (!Array.isArray(params)) return;
    if (!params.includes('countryRegulation')) return;
    let data = null;
    if (params.includes('PROGRAMA_SOCIAL')) {
      data = {};
    }
    if (params.includes('AUTOEXCLUSAO_CENTRALIZADA')) {
      data = { centralIzada: true };
    }
    if (data !== null) {
      _openDialog({
        dialogType: EnumDialogsKeys.SOCIAL_PROGRAM_POPUP,
        dialogProps: { data },
      });
    }
  };

  const handleRegSuccess = (): void => {
    setSuccessfullyReged(true);
    sessionStorage.removeItem('signUpBonusCode');
    sessionStorage.removeItem('signInBonusCode');
  };

  const handleRegVerification = (res: any): void => {
    const phoneNumberField = getField(formValues, 'phoneNumber');
    const phoneNumberValue = phoneNumberField?.phoneNumber?.value;
    const endTime = Date.now() + res?.otpExpiresIn;

    const data = {
      endTime,
      resendOtpAfter: Date.now() + res?.resendOtpAfter,
      requestIdentifier: res?.requestIdentifier,
    };
    _openDialog({
      dialogType: EnumDialogsKeys.OTP_REQUEST_PHONE_VIA_SERVER_POPUP,
      dialogProps: { phoneNumber: phoneCode.slice(1) + phoneNumberValue, data },
    });
  };

  // Resolves to whether the player can move on: terms confirmed, or nothing to confirm
  const sendTermsAcceptance = (username: string): Promise<boolean> => {
    if (!isTermsCheckEnabled || !username) return Promise.resolve(true);

    return confirmTermsAcceptance({
      skinId,
      identifierKey: 'username',
      identifierValue: username,
      websiteVersion: packageJson.version,
    })
      .then((res) => Boolean(res?.success))
      .catch((error) => {
        console.error('confirmTermsAcceptance failed:', error);

        return false;
      });
  };

  const handleRegResponse = (res: any, username: string): void => {
    if (res?.message === 'verify_email') {
      setCheckEmail(true);
    } else if (res.message?.key === 'unprocessable_entity') {
      handleUnprocessableEntityErrors(res.message);
    } else if (res?.pendingOtpVerification && process.env.REACT_APP_OTP) {
      handleRegVerification(res);
    } else if (res?.success) {
      registeredUsernameRef.current = username;
      if (!isKycEnableForReg) {
        sendTermsAcceptance(username).catch((e) => console.log(e));
      }
      handleRegSuccess();
    }
  };

  const handleSubmit = (): void => {
    handleSetErrors();
    scrollToTop();

    const payload: any = makeSignUpPayload(formValues, affiliateId || '');
    const { bankName, bankCode, bankAccountType, bankAccountNumber, phoneNumber, ...restPayload } = payload;

    // Validate payload
    const isValid =
      payloadIsValid(payload, requiredFields, setLocaleErrorsState, t) && !Object.keys(signUpErrors).length;
    if (!isValid) return;

    setSignUpRequestLoading(true);

    const cleanedPayload = { ...restPayload };

    if (phoneNumber && phoneCode) {
      cleanedPayload.phoneNumber = phoneCode.slice(1) + phoneNumber;
    }

    // Add banksInformation if bank details exist
    if (bankName && bankCode && bankAccountType && bankAccountNumber) {
      cleanedPayload.banksInformation = [{ bankName, bankCode, bankAccountType, bankAccountNumber }];
    }

    signUpRequest(cleanedPayload)
      .then((res) => handleRegResponse(res, payload.username))
      .finally(() => {
        setSignUpRequestLoading(false);
      });
  };

  const getDefaultCategoryPath = (): string => {
    return CASINO_CATEGORIES.includes(defaultCategory as string) ? `/category/${defaultCategory}` : '/';
  };

  const applyDefaultCategory = (): void => {
    if (CASINO_CATEGORIES.includes(defaultCategory as string)) {
      _setSelectedCategory(defaultCategory);
    }
  };

  const trackRegistrationSuccess = (): void => {
    sessionRecorder?.trackEvent('Registration complete');
  };

  const autoSignIn = (): void => {
    scrollToTop();
    const payload: any = makeSignUpPayload(formValues, affiliateId || '');
    const authClientAppId = sessionStorage.getItem('authClientAppId') as string;
    _signInReq({
      data: makeSignInPayload({
        username: payload.username,
        password: payload.password,
        authClientAppId: urlQuery.authClientAppId || authClientAppId,
      }),
      onSuccessCb: () => {
        const user = localStorage.getItem('user');

        if (!user) return;

        if (authClientAppId) {
          sessionStorage.removeItem('authClientAppId');
        }

        if (isLimitsEnabled) {
          getCurrentPlayerLimits().catch((e) => console.log(e));
        }

        trackRegistrationSuccess();

        if (generalConfigs?.redirectToSport) {
          // URL uses the slug (sport); selectedCategory keeps the data key (betting)
          navigate('/category/sport');
          _setSelectedCategory('betting');
        }
      },
      dispatch,
    });
    const targetPath = getDefaultCategoryPath();
    setTimeout(() => {
      applyDefaultCategory();
      navigate(targetPath);
    }, 0);
  };

  const kycVerification = async (): Promise<void> => {
    if (isKycEnableForReg && LEGITIMUZ_TOKEN) {
      // A closed popup stays mounted and listening, so without this both instances reach autoSignIn
      activeKycSdkInstance?.unmount?.();
      kycSuccessHandled = false;

      const sdkInstance = (window as any)?.Legitimuz({
        token: LEGITIMUZ_TOKEN,
        host: 'https://api.legitimuz.com',
        onSuccess: (eventName: string) => {
          if (eventName !== 'facematch') return;
          if (kycSuccessHandled) return;
          kycSuccessHandled = true;

          setTimeout(() => {
            const username =
              registeredUsernameRef.current || makeSignUpPayload(formValues, affiliateId || '')?.username;

            sendTermsAcceptance(username)
              .then((termsConfirmed) => {
                if (termsConfirmed) {
                  autoSignIn();
                }
              })
              .catch((e) => console.log(e));
          }, 1000);
        },
      });

      activeKycSdkInstance = sdkInstance;
      sdkInstance.mount();
      sdkInstance.verifyDocument({ cpf: cpfValue });
    }
  };

  const closeSuccessfullyRegedModal = (): void => {
    if (isKycEnableForReg) {
      kycVerification().catch((e) => console.log(e));
    } else {
      autoSignIn();
    }
  };

  const handleCheckErrors = useCallback(() => {
    if (localErrorsState) {
      setFormValues((prevState: any) => {
        const newState = [...prevState];

        newState.forEach((obj: any, index: number) => {
          Object.keys(localErrorsState).forEach((key: string) => {
            if (obj.hasOwnProperty(key)) {
              newState[index][key].error = localErrorsState[key];
            }
          });
        });

        return newState;
      });
    }
  }, [localErrorsState, formValues]);

  const handleSetErrors = useCallback(() => {
    setFormValues((prevState: any) => {
      const newState = [...prevState];
      const fieldsWithOwnErrors = ['username', 'password', 'password2', 'phoneNumber', 'email'];

      newState.forEach((obj: any, index: number) => {
        Object.keys(obj).forEach((key: string): any => {
          if (obj[key].required && !Boolean(obj[key].value) && !fieldsWithOwnErrors.includes(key)) {
            newState[index][key].error = setLocaleErrorsState((prev: any) => ({
              ...prev,
              [key]: `${key}_field_required`,
            }));

            return null;
          }

          if (key === 'password2' && obj[key]?.value !== obj['password']?.value) {
            setLocaleErrorsState({
              ...localErrorsState,
              password2: t('password_must_be_a_match'),
            });

            return null;
          }
        });
      });

      return newState;
    });
  }, [formValues]);

  const validateData = (data1: { [key: string]: string }, data2: { [key: string]: string }): boolean => {
    for (const key in data2) {
      if (data1 && key in data1) {
        return true;
      }
    }
    return false;
  };

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>, index: number): void => {
      const { name, value, checked } = e.target;

      setLocaleErrorsState(null);
      setFormValues((prevState: any) => {
        const newState = [...prevState];
        if (newState[index][name]) {
          newState[index][name].value = value;
          newState[index][name].error = null;
        }

        if (name === 'notificationsEnabled') {
          newState[index][name].value = checked ? checked : false;
        }

        if (name === 'password') {
          newState[index]['password2'].error = null;
        }

        if (name === 'password2') {
          newState[index]['password'].error = null;
        }

        return newState;
      });
    },
    [formValues]
  );

  const disableHandleSubmit = useMemo(() => {
    let flag = false;

    if (formValues?.[activeStep - 1]) {
      const stepInputs = Object.values(formValues[activeStep - 1]);
      flag = stepInputs.map((m: any) => m.required && m.value?.length === 0).includes(true);
    }

    const hasError = validateData(formValues?.[activeStep - 1], signUpErrors);
    if (hasError) {
      flag = true;
    }

    if (!activateButton && formValues?.[activeStep - 1]?.cpf) {
      flag = true;
    }

    return flag;
  }, [formValues, registrationSteps, activeStep, signUpErrors, activateButton]);

  useEffect(() => {
    if (activateNextStep && isEnableOtp) {
      setActiveStep(2);
    }
  }, [activateNextStep, isEnableOtp]);

  useEffect(() => {
    if (activateFinalNextStep && isEnablePhoneNumberOtp) {
      setActiveStep(3);
    }
  }, [activateFinalNextStep, isEnablePhoneNumberOtp]);

  const renderSteperNav = useCallback(() => {
    const disabled = !Boolean(formValues.length) || loading;

    return (
      <div className="buttons_container">
        {showPreviousBtn && activeStep > 1 && (
          <Button onClick={() => handleNavigateInStepper(activeStep - 1)} disabled={disabled}>
            {t('button_previous')}
          </Button>
        )}
        {activeStep < Number(registrationSteps?.length) && (
          <Button
            className={classes}
            onClick={() => handleNavigateInStepper(activeStep + 1)}
            disabled={disableHandleSubmit}
          >
            {t('button_next')}
          </Button>
        )}
        {activeStep === Number(registrationSteps?.length) && (
          <Button
            className={classes}
            disabled={disableHandleSubmit}
            loading={signUpRequestLoading}
            onClick={handleSubmit}
            id="registration-submit-btn"
          >
            {t('register')}
          </Button>
        )}
      </div>
    );
  }, [
    registrationSteps,
    activeStep,
    signUpErrors,
    formValues,
    loading,
    activateButton,
    disableHandleSubmit,
    signUpRequestLoading,
  ]);

  const steps = useMemo(() => {
    return registrationSteps?.map((_, i) => i + 1);
  }, [registrationSteps]);

  const renderForm = (): JSX.Element => {
    if (checkEmail || successfullyReged || (successfullyRegFromOtp && process.env.REACT_APP_OTP)) {
      return (
        <SuccessfullyReged
          steps={steps}
          classes={classes}
          onSubmit={closeSuccessfullyRegedModal}
          title={isKycEnableForReg ? t('KYC_successFullRegister') : t('successFullRegister')}
        />
      );
    } else {
      return (
        <SignupForm
          formValues={formValues}
          activeStep={activeStep}
          affiliateId={affiliateId}
          styles={styles}
          forgotLink={forgotLink}
          signIn={signIn}
          changeContent={changeContent}
          signUpErrors={signUpErrors}
          registrationSteps={registrationSteps}
          renderSteperNav={renderSteperNav}
          ref={formRef}
        />
      );
    }
  };

  const getInitialValue = (type: string, isRequired: boolean): string | boolean | number => {
    if (['password', 'string', 'select'].includes(type) || isRequired) return '';
    if (type === 'boolean') return false;
    return 0;
  };

  useEffect(() => {
    const reqFields: any = [];
    const _ = registrationSteps
      ? registrationSteps?.map((stepData, index: number) => {
          const stepObjKeys = Object.keys(stepData);

          return stepObjKeys.reduce((acc, cur: string) => {
            const type = stepData[cur].type;
            const isRequired = stepData[cur].required;

            if (isRequired) {
              reqFields.push(cur);
            }
            const value = getInitialValue(type, isRequired);

            acc[cur] = {
              ...stepData[cur],
              value: value,
              onChange: (e: ChangeEvent<HTMLInputElement>) => handleChange(e, index),
              type: cur.includes('password') ? 'password' : type,
            };

            return acc;
          }, {});
        })
      : [];

    setRequiredFields(reqFields);
    setFormValues(_);
  }, [registrationSteps]);

  useEffect(() => {
    handleCheckErrors();
  }, [localErrorsState]);

  useEffect(() => {
    _setError({});

    return () => {
      if ((registrationSteps?.[0] as any)?.cpf) {
        _setCPF_User(null);
      }

      _toggleActivateBtn(false);
    };
  }, []);

  useEffect(() => {
    // TO DO remove cpf check
    if (!Object.keys(signUpErrors).length && (steps?.length === 1 || !(registrationSteps?.[0] as any)?.cpf)) {
      _toggleActivateBtn(true);
    }
  }, [registrationSteps]);

  useEnterSubmit('registration-submit-btn');

  return (
    <>
      {activeStep !== 1 && !successfullyReged && !checkEmail && !isEnableOtp ? (
        <div className="back_icon" onClick={() => handleNavigateInStepper(activeStep - 1)}>
          <SvgIcon pointer src={icons.arrowDown} color="white" />
          <span>{t('back')}</span>
        </div>
      ) : null}

      {renderForm()}
    </>
  );
};

export default SignUpContent;
